import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useRenameConversation } from "../../backend/queries/chat";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { TOUCH_CLOSE } from "./dialogClose";
import { useFocusReturn } from "./useFocusReturn";

type RenameGroupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  currentTitle: string;
};

/**
 * Staff-only flow for renaming a group. The API refuses clients and direct
 * messages, so the page only offers this to staff on a group.
 */
export function RenameGroupDialog({
  open,
  onOpenChange,
  conversationId,
  currentTitle,
}: RenameGroupDialogProps) {
  const focusReturn = useFocusReturn(open);
  const [title, setTitle] = useState(currentTitle);
  const [error, setError] = useState<string | null>(null);
  const rename = useRenameConversation(conversationId);

  useEffect(() => {
    if (open) {
      setTitle(currentTitle);
      setError(null);
    }
  }, [open, currentTitle]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next = title.trim();
    if (!next) {
      setError("A group needs a name.");
      return;
    }
    try {
      await rename.mutateAsync(next);
      onOpenChange(false);
    } catch (renameError) {
      setError(
        renameError instanceof Error
          ? renameError.message
          : "The group could not be renamed.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
          className="grid-cols-[minmax(0,1fr)] sm:max-w-md"
          closeClassName={TOUCH_CLOSE} {...focusReturn}>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Rename group</DialogTitle>
            <DialogDescription>
              Everyone in the group sees the new name.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="chat-rename-title">Group name</Label>
            <Input
              id="chat-rename-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={120}
              autoComplete="off"
            />
          </div>

          {error ? (
            <p role="alert" className="text-sm text-tone-danger-foreground">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="min-h-touch md:min-h-0"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="min-h-touch md:min-h-0"
              disabled={rename.isPending}
            >
              {rename.isPending ? (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              ) : null}
              Save name
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
